import { useCustom, useGo } from '@refinedev/core';
import { Users, CheckCircle2, TrendingUp, Loader2, BarChart3 } from 'lucide-react';
import { notify } from '@/lib/notify';
import { useEffect, type ReactNode } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/components/universal';

interface BookAnalyticsRecentReader {
  userId: number;
  userName: string;
  userAvatar: string | null;
  percent: number;
  lastOpenedAt: string;
  completedAt: string | null;
}

interface BookAnalytics {
  totalUsersStarted: number;
  totalUsersCompleted: number;
  averageProgressPercent: number;
  recentReaders: BookAnalyticsRecentReader[];
}

interface BookAnalyticsSectionProps {
  bookId: string | number;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? '';
  return (first + second).toUpperCase() || '?';
}

export function BookAnalyticsSection({ bookId }: BookAnalyticsSectionProps) {
  const go = useGo();

  const analyticsQuery = useCustom<BookAnalytics>({
    url: `/admin/books/${bookId}/analytics`,
    method: 'get',
  });

  const query = analyticsQuery.query;
  const analytics = query?.data?.data;
  const error = query?.error;
  const isLoading = query?.isLoading ?? false;

  useEffect(() => {
    if (error) {
      const message = error instanceof Error ? error.message : 'Не удалось загрузить аналитику';
      notify.error(message);
    }
  }, [error]);

  const roundedAvg = analytics ? Math.round(analytics.averageProgressPercent) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4" />
          Аналитика
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Skeleton className="h-[68px] rounded-xl" />
            <Skeleton className="h-[68px] rounded-xl" />
            <Skeleton className="h-[68px] rounded-xl" />
          </div>
        ) : analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <Users className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{analytics.totalUsersStarted}</p>
                <p className="text-[11px] text-muted-foreground">Начали читать</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{analytics.totalUsersCompleted}</p>
                <p className="text-[11px] text-muted-foreground">Закончили</p>
              </div>
            </div>
            <div className="rounded-xl border bg-card p-4 flex items-center gap-3">
              <TrendingUp className="h-4 w-4 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xl font-semibold">{roundedAvg}%</p>
                <p className="text-[11px] text-muted-foreground">Средний прогресс</p>
              </div>
            </div>
          </div>
        )}

        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
            {`Недавно читающие${analytics ? ` · ${analytics.recentReaders.length}` : ''}`}
          </p>
          {renderRecentReaders(isLoading, analytics, go)}
        </div>
      </CardContent>
    </Card>
  );
}

function renderRecentReaders(
  isLoading: boolean,
  analytics: BookAnalytics | undefined,
  go: ReturnType<typeof useGo>,
): ReactNode {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!analytics || analytics.recentReaders.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-12 text-sm">
        Пока нет читателей
      </div>
    );
  }
  return (
    <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Пользователь</TableHead>
                <TableHead>Прогресс</TableHead>
                <TableHead>Последнее открытие</TableHead>
                <TableHead>Статус</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {analytics.recentReaders.map((reader) => {
                const percent = Math.round(reader.percent);
                return (
                  <TableRow
                    key={reader.userId}
                    className="cursor-pointer"
                    onClick={() => go({ to: `/users/show/${reader.userId}` })}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          {reader.userAvatar ? (
                            <AvatarImage src={reader.userAvatar} alt={reader.userName} />
                          ) : null}
                          <AvatarFallback className="text-[10px]">
                            {getInitials(reader.userName)}
                          </AvatarFallback>
                        </Avatar>
                        <p className="font-medium text-sm">{reader.userName}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <Progress value={percent} className="h-1.5 flex-1" />
                        <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">
                          {percent}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(reader.lastOpenedAt)}
                    </TableCell>
                    <TableCell>
                      {reader.completedAt ? (
                        <Badge variant="default" className="text-[10px]">Завершил</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">Читает</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
    </TableBody>
  </Table>
  );
}
